import { Injectable } from '@angular/core'
import { formatNumber } from '@angular/common'
import { IShopInfo } from '@lla-platform/core/core-data-access'
import { isEmpty, isObject } from 'lodash'
import { ToastrService } from 'ngx-toastr'

const AVATAR_COLORS = [
  '#4682B4',
  '#3CB371',
  '#DAA520',
  '#BA55D3',
  '#FF7F50',
  '#20B2AA',
  '#7B68EE',
  '#F4A460',
  '#FF6347',
  '#6B8E23'
]

@Injectable({
  providedIn: 'root'
})
export class CommonService {
  constructor(private toastrService: ToastrService) {}

  /**
   * Extracts initials from a full name.
   * Strips parenthetical content (e.g., "Jamil Marshall (People Business Partner)" → "JM").
   */
  getInitials(name: string): string {
    const clean = name.replace(/\s*\(.*?\)\s*/g, '').trim()
    const parts = clean.split(/\s+/)
    const first = parts[0]?.[0] ?? ''
    const last = parts.length > 1 ? parts[parts.length - 1][0] : ''
    return (first + last).toUpperCase()
  }

  /**
   * Returns a deterministic color from a name, suitable for initials avatar backgrounds.
   * Same input always produces the same color.
   */
  getAvatarColor(name: string): string {
    let hash = 0
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash)
    }
    return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length]
  }

  /**
   * Returns a MediaQueryList for a Tailwind breakpoint name (e.g. 'lg', '2xl').
   * Use `.matches` for a one-shot check, or `.addEventListener('change', ...)` to subscribe.
   */
  getBreakpointMediaQuery(name: string): MediaQueryList {
    const value = getComputedStyle(document.documentElement)
      .getPropertyValue(`--breakpoint-${name}`)
      .trim()
    return window.matchMedia(`(min-width: ${value})`)
  }

  /**
   * Quick boolean check: does the current viewport match or exceed the named Tailwind breakpoint?
   */
  matchesBreakpoint(name: string): boolean {
    return this.getBreakpointMediaQuery(name).matches
  }

  /**
   * Converts an HTML fragment to plain text. Block boundaries (<br>, closing
   * p/div/li/ul/ol/heading tags) become newlines so line structure survives.
   */
  htmlToPlainText(html: string): string {
    if (!html) {
      return ''
    }
    const withBreaks = html
      .replace(/<br\s*\/?>/gi, '\n')
      .replace(/<\/(p|div|li|ul|ol|h[1-6]|blockquote)>/gi, '\n')
    const doc = new DOMParser().parseFromString(withBreaks, 'text/html')
    return (doc.body.textContent ?? '').replace(/\n\s*\n/g, '\n').trim()
  }

  copyToClipboard(text: string, message = 'Copied to clipboard successfully') {
    navigator.clipboard.writeText(text).then(() => {
      this.toastrService.success(message)
    })
  }

  convertEmptyToNull(payload: any) {
    const objectKeys = Object.keys(payload)
    for (const key of objectKeys) {
      const value = payload[key]
      if (isObject(value) && isEmpty(value)) {
        payload[key] = null
      }
    }
    return payload
  }

  generateRandomColor() {
    const letters = '0123456789ABCDEF'
    let color = '#'
    for (let i = 0; i < 6; i++) {
      color += letters[Math.floor(Math.random() * 16)]
    }
    return color
  }

  generateRandomString() {
    return (Math.random() + 1).toString(36).substring(2)
  }

  blobToBase64(file: File): Promise<string> {
    return new Promise((resolve, _) => {
      const reader = new FileReader()
      reader.onloadend = () => resolve(reader.result as string)
      reader.readAsDataURL(file)
    })
  }

  async resizeImage(file: File, opts: { maxDimension: number; quality: number }): Promise<File> {
    const url = URL.createObjectURL(file)
    try {
      const img = await new Promise<HTMLImageElement>((resolve, reject) => {
        const i = new Image()
        i.onload = () => resolve(i)
        i.onerror = () => reject(new Error('Failed to decode image'))
        i.src = url
      })

      const longest = Math.max(img.naturalWidth, img.naturalHeight)
      const scale = longest > opts.maxDimension ? opts.maxDimension / longest : 1
      const targetW = Math.max(1, Math.round(img.naturalWidth * scale))
      const targetH = Math.max(1, Math.round(img.naturalHeight * scale))

      const canvas = document.createElement('canvas')
      canvas.width = targetW
      canvas.height = targetH
      const ctx = canvas.getContext('2d')
      if (!ctx) throw new Error('Canvas 2D context unavailable')
      ctx.drawImage(img, 0, 0, targetW, targetH)

      const blob = await new Promise<Blob | null>((resolve) => {
        canvas.toBlob((b) => resolve(b), 'image/jpeg', opts.quality)
      })
      if (!blob) throw new Error('Failed to encode image')

      const baseName = file.name.replace(/\.[^.]+$/, '')
      return new File([blob], `${baseName}.jpg`, {
        type: 'image/jpeg',
        lastModified: Date.now()
      })
    } finally {
      URL.revokeObjectURL(url)
    }
  }

  filterData<T extends IShopInfo>(
    data: T[],
    state?: string | 'all',
    division?: string | 'all',
    shopIds?: string[]
  ): T[] {
    let result: T[] = [...data]
    if (state) {
      result =
        state !== '' && state !== 'all'
          ? result.filter((el) => el.locationInfo?.state === state)
          : [...result]
    }

    if (division) {
      result =
        division !== '' && division !== 'all'
          ? result.filter((el) => el.locationInfo?.division === division)
          : [...result]
    }

    if (shopIds && shopIds.length > 0) {
      result = result.filter((el) => (el.locationInfo?.id ? shopIds.includes(el.locationInfo.id) : true))
    }

    return result
  }

  formateMins(value: number): string {
    const minutes = value % 60
    const hours = (value - minutes) / 60
    return (
      (hours < 10 ? '0' : '') +
      hours.toFixed() +
      ':' +
      (minutes < 10 ? '0' : '') +
      minutes.toFixed() +
      'h'
    )
  }

  formatNumbers(value: number, decimals = 2): string {
    const format = `1.${decimals}-${decimals}`
    if (value >= 1000000) {
      const inMillions = value / 1000000
      return formatNumber(inMillions, 'en-US', format) + 'M'
    } else if (value >= 1000) {
      const inThousands = value / 1000
      return formatNumber(inThousands, 'en-US', format) + 'K'
    } else {
      return formatNumber(value, 'en-US', '1.0-0')
    }
  }

  /**
   * Rounds a number to two decimal places.
   * @param value The number to round.
   * @returns The rounded number.
   */
  roundToTwoDecimals(value: number): number {
    return Math.round(value * 100) / 100
  }
}
